import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import App from './App.tsx';
import './index.css';

// EXPERT CLINICAL COMMENTARY: Globally monkey-patch window.fetch, XMLHttpRequest, and WebSocket
// to automatically and securely rewrite relative '/api/' and '/socket.io/' calls to the live Cloud Run backend
// when running on custom production domains (such as iq-dr.com or firebaseapp.com).
// This guarantees seamless auto-updating and real-time telehealth connectivity without breaking local dev mode.
if (typeof window !== "undefined") {
  // 1. Safe fetch patch
  try {
    const originalFetch = window.fetch;
    if (originalFetch) {
      const customFetch = function (this: any, input: any, init?: any) {
        if (typeof input === "string") {
          const hostname = window.location.hostname;
          const isLocalOrDev = hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes("192.168.") || hostname.includes("ais-dev-");
          if (!isLocalOrDev) {
            if (input.startsWith("/socket.io/")) {
              input = "https://iq-doctor-backend-51781980513.europe-west3.run.app" + input;
            }
          }
        }
        return originalFetch.call(this, input, init);
      };

      try {
        Object.defineProperty(window, 'fetch', {
          value: customFetch,
          writable: true,
          configurable: true,
          enumerable: true
        });
      } catch (e) {
        try {
          (window as any).fetch = customFetch;
        } catch (err) {
          console.warn("Could not patch window.fetch directly:", err);
        }
      }
    }
  } catch (e) {
    console.warn("Fetch monkey-patch error caught:", e);
  }

  // 2. Safe XMLHttpRequest patch
  try {
    if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
      const originalOpen = window.XMLHttpRequest.prototype.open;
      window.XMLHttpRequest.prototype.open = function (method, url, ...args) {
        if (typeof url === "string") {
          const hostname = window.location.hostname;
          const isLocalOrDev = hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes("192.168.") || hostname.includes("ais-dev-");
          if (!isLocalOrDev) {
            if (url.startsWith("/socket.io/")) {
              url = "https://iq-doctor-backend-51781980513.europe-west3.run.app" + url;
            }
          }
        }
        return originalOpen.call(this, method, url, ...args as any);
      };
    }
  } catch (e) {
    console.warn("XMLHttpRequest monkey-patch error caught:", e);
  }

  // 3. Safe WebSocket patch
  try {
    const OriginalWebSocket = window.WebSocket;
    if (OriginalWebSocket) {
      // @ts-ignore
      const customWebSocket = function (url, protocols) {
        if (typeof url === "string") {
          const hostname = window.location.hostname;
          const isLocalOrDev = hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes("192.168.") || hostname.includes("ais-dev-");
          if (!isLocalOrDev) {
            if (url.includes("/socket.io/")) {
              const directUrl = url.replace(window.location.host, "iq-doctor-backend-51781980513.europe-west3.run.app");
              return new OriginalWebSocket(directUrl, protocols);
            }
          }
        }
        return new OriginalWebSocket(url, protocols);
      };

      Object.getOwnPropertyNames(OriginalWebSocket).forEach(prop => {
        if (!(prop in customWebSocket)) {
          try {
            // @ts-ignore
            customWebSocket[prop] = OriginalWebSocket[prop];
          } catch (e) {}
        }
      });

      try {
        Object.defineProperty(window, 'WebSocket', {
          value: customWebSocket,
          writable: true,
          configurable: true,
          enumerable: true
        });
      } catch (e) {
        try {
          // @ts-ignore
          window.WebSocket = customWebSocket;
        } catch (err) {
          console.warn("Could not patch window.WebSocket directly:", err);
        }
      }
    }
  } catch (e) {
    console.warn("WebSocket monkey-patch error caught:", e);
  }
}

// EXPERT CLINICAL COMMENTARY: Globally monkey-patch Response.prototype.json
// to safely handle plain-text "Rate exceeded." responses from proxies or API limits.
// This prevents uncaught "Unexpected token 'R', 'Rate exceeded.' is not valid JSON" errors
// from crashing the clinic dashboard interface.
const originalJson = Response.prototype.json;
Response.prototype.json = async function () {
  const cloned = this.clone();
  try {
    const text = await cloned.text();
    if (text && text.trim() === "Rate exceeded.") {
      return { error: "Rate limit exceeded. Please try again in a few seconds.", status: "RATE_LIMIT_EXCEEDED" };
    }
    return JSON.parse(text);
  } catch (err) {
    // If clone-and-parse fails, delegate to the original implementation to maintain exact standard spec behavior
    return originalJson.call(this);
  }
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
